// eslint-disable-next-line jira/restricted/react
import React, { PureComponent, type SyntheticEvent } from 'react';
import flow from 'lodash/flow';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@atlaskit/modal-dialog';
import AnalyticsData from '@atlassian/jira-analytics-web-react/src/components/analytics-data.tsx';
import { AnalyticsSource } from '@atlassian/jira-analytics-web-react/src/components/decorators.tsx';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { MODAL } from '@atlassian/jira-product-analytics-bridge';
import commonMessages from '@atlassian/jira-servicedesk-common/src/i18n';
import { SUBMIT_INITIATED_STATUS } from '@atlassian/jira-servicedesk-common/src/ui/components/submit-button/model.tsx';
import SubmitButton from '@atlassian/jira-servicedesk-common/src/ui/components/submit-button/view.tsx';
import { ANALYTIC_CONTEXTS } from '@atlassian/jira-servicedesk-common/src/utils/analytics';
import messages from './messages';
import { FooterButtonsContainer } from './styled';

export type Props = {
	isOpen: boolean;
	isDeleting: boolean;
	onDelete: (analyticsEvent: UIAnalyticsEvent) => void;
	onCancel: () => void;
	intl: IntlShape;
};

// eslint-disable-next-line jira/react/no-class-components
export class DeleteDialog extends PureComponent<Props> {
	static defaultProps: Partial<Props> = {
		isOpen: false,
	};

	onDelete = (e: SyntheticEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		this.props.onDelete(analyticsEvent);
	};

	// eslint-disable-next-line jira/react-no-unneeded-arrow-function-properties
	renderHeader = () => {
		const {
			intl: { formatMessage },
		} = this.props;
		return <ModalTitle>{formatMessage(messages.deleteRuleTitle)}</ModalTitle>;
	};

	// eslint-disable-next-line jira/react-no-unneeded-arrow-function-properties
	renderFooter = () => {
		const {
			intl: { formatMessage },
			isDeleting,
			onCancel,
		} = this.props;
		return (
			<FooterButtonsContainer>
				<SubmitButton
					onSubmit={this.onDelete}
					submitStatus={isDeleting ? SUBMIT_INITIATED_STATUS : null}
					buttonTextDescriptor={commonMessages.delete}
				/>
				<Button onClick={onCancel} appearance="link" isDisabled={isDeleting}>
					{formatMessage(commonMessages.cancel)}
				</Button>
			</FooterButtonsContainer>
		);
	};

	render() {
		const {
			intl: { formatMessage },
			isOpen,
		} = this.props;
		return (
			isOpen && (
				<>
					<ViewTracker />
					<ShortcutScope>
						<ModalDialog>
							<ModalHeader>{this.renderHeader()}</ModalHeader>
							<ModalBody>
								<>{formatMessage(messages.deleteRuleMessage)}</>
							</ModalBody>
							<ModalFooter>{this.renderFooter()}</ModalFooter>
						</ModalDialog>
					</ShortcutScope>
				</>
			)
		);
	}
}

export default flow(
	AnalyticsSource('deleteAutomationRule', MODAL),
	AnalyticsData({
		attributes: {
			context: ANALYTIC_CONTEXTS.AUTOMATION_SETTINGS,
		},
	}),
	injectIntl,
)(DeleteDialog);
